/**
 * saved-views.tsx — "Chế độ xem tuỳ chỉnh" trong cột bộ lọc Hộp thư (kiểu SaleSmartly).
 *
 *  - Chế độ xem cá nhân: chỉ mình thấy.
 *  - Chế độ xem chung: cả tổ chức thấy; tạo/đổi phạm vi chỉ Chủ sở hữu / Quản trị viên / Quản lý.
 *
 * Bấm một chế độ xem → danh sách lấy tên làm tiêu đề và áp điều kiện của nó.
 */
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import {
  ChevronDown,
  ChevronRight,
  Filter,
  Lock,
  MoreHorizontal,
  Pencil,
  Plus,
  Star,
  Trash2,
  Users,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/misc'
import { apiError } from '@/lib/api-client'
import { cn } from '@/lib/utils'
import {
  useCreateInboxView,
  useDeleteInboxView,
  useInboxViews,
  useUpdateInboxView,
  type InboxSavedView,
  type ViewScope,
} from '@/hooks/use-inbox-views'
import { AddConditionButton, ConditionRows } from './condition-editor'
import { cleanConditions, type ConditionLogic, type InboxCondition } from './inbox-conditions'

const MAX_NAME = 60

// ─────────────────────────────────────────────────────────────────────────────
// Hộp thoại tạo / sửa / đổi tên
// ─────────────────────────────────────────────────────────────────────────────

export type ViewDialogState =
  | { open: false }
  | { open: true; mode: 'create'; conditions: InboxCondition[]; logic: ConditionLogic }
  | { open: true; mode: 'edit' | 'rename'; view: InboxSavedView }

export function ViewDialog({
  state,
  onClose,
  onSaved,
}: {
  state: ViewDialogState
  onClose: () => void
  onSaved?: (view: InboxSavedView) => void
}) {
  const { t } = useTranslation('convList')
  const { data } = useInboxViews()
  const canManageShared = data?.canManageShared ?? false
  const create = useCreateInboxView()
  const update = useUpdateInboxView()
  const [name, setName] = useState('')
  const [scope, setScope] = useState<ViewScope>('personal')
  const [conditions, setConditions] = useState<InboxCondition[]>([])
  const [logic, setLogic] = useState<ConditionLogic>('and')

  // Nạp lại mỗi lần mở.
  useEffect(() => {
    if (!state.open) return
    if (state.mode === 'create') {
      setName('')
      setScope('personal')
      setConditions(state.conditions.map((c) => ({ ...c })))
      setLogic(state.logic)
    } else {
      setName(state.view.name)
      setScope(state.view.scope)
      setConditions(state.view.conditions.map((c) => ({ ...c })))
      setLogic(state.view.logic)
    }
  }, [state])

  if (!state.open) return null
  const mode = state.mode
  const showConditions = mode !== 'rename'
  const showScope = mode === 'create' || (mode === 'edit' && canManageShared)
  const busy = create.isPending || update.isPending
  const trimmed = name.trim()

  const submit = async () => {
    if (!trimmed) return toast.error(t('views.enterName'))
    try {
      const saved =
        mode === 'create'
          ? await create.mutateAsync({ name: trimmed, scope, conditions: cleanConditions(conditions), logic })
          : await update.mutateAsync({
              id: state.view.id,
              name: trimmed,
              ...(mode === 'edit' ? { conditions: cleanConditions(conditions), logic } : {}),
              ...(mode === 'edit' && canManageShared && scope !== state.view.scope ? { scope } : {}),
            })
      toast.success(mode === 'create' ? t('views.saved') : t('views.updated'))
      onSaved?.(saved)
      onClose()
    } catch (e) {
      toast.error(apiError(e))
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className={cn(showConditions ? 'max-w-3xl' : 'max-w-md')}>
        <DialogHeader>
          <DialogTitle>
            {mode === 'create' ? t('views.titleCreate') : mode === 'rename' ? t('views.titleRename') : t('views.titleEdit')}
          </DialogTitle>
          {mode === 'create' && (
            <DialogDescription>{t('views.createDesc')}</DialogDescription>
          )}
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="view-name">{t('views.nameLabel')}</Label>
            <Input
              id="view-name"
              value={name}
              maxLength={MAX_NAME}
              autoFocus
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !showConditions && submit()}
              placeholder={t('views.namePlaceholder')}
            />
          </div>

          {showScope && (
            <div className="space-y-1.5">
              <Label>{t('views.scope')}</Label>
              <div className="grid grid-cols-2 gap-2">
                <ScopeOption
                  active={scope === 'personal'}
                  icon={Lock}
                  title={t('views.scopePersonal')}
                  hint={t('views.scopePersonalHint')}
                  onClick={() => setScope('personal')}
                />
                <ScopeOption
                  active={scope === 'shared'}
                  icon={Users}
                  title={t('views.scopeShared')}
                  hint={canManageShared ? t('views.scopeSharedHint') : t('views.scopeSharedLocked')}
                  disabled={!canManageShared}
                  onClick={() => setScope('shared')}
                />
              </div>
            </div>
          )}

          {showConditions && (
            <div className="space-y-2">
              <Label>{t('views.conditions')}</Label>
              <ConditionRows conditions={conditions} logic={logic} onChange={setConditions} onLogicChange={setLogic} />
              <AddConditionButton onAdd={(c) => setConditions((d) => [...d, c])} />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('common:actions.cancel')}
          </Button>
          <Button onClick={submit} disabled={busy || !trimmed}>
            {mode === 'create' ? t('common:actions.save') : t('common:actions.update')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function ScopeOption({
  active,
  icon: Icon,
  title,
  hint,
  disabled,
  onClick,
}: {
  active: boolean
  icon: typeof Lock
  title: string
  hint: string
  disabled?: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'flex items-start gap-2 rounded-lg border p-3 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-50',
        active ? 'border-primary bg-accent' : 'hover:border-primary/40',
      )}
    >
      <Icon className={cn('mt-0.5 h-4 w-4 shrink-0', active ? 'text-primary' : 'text-muted-foreground')} />
      <span>
        <span className="block text-sm font-medium">{title}</span>
        <span className="block text-xs text-muted-foreground">{hint}</span>
      </span>
    </button>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Mục trong cột bộ lọc
// ─────────────────────────────────────────────────────────────────────────────

export interface SavedViewsSectionProps {
  expanded: boolean
  activeId?: string
  counts?: Record<string, number | null>
  onSelect: (view: InboxSavedView) => void
  /** Bỏ chọn khi chế độ xem đang mở bị xoá. */
  onDeleted: (id: string) => void
  onOpenDialog: (s: ViewDialogState) => void
}

const OPEN_KEY = 'ecdp_inbox_views_open'

function loadOpen(): { personal: boolean; shared: boolean } {
  try {
    const v = JSON.parse(localStorage.getItem(OPEN_KEY) ?? 'null') as { personal?: boolean; shared?: boolean } | null
    return { personal: v?.personal ?? true, shared: v?.shared ?? true }
  } catch {
    return { personal: true, shared: true }
  }
}

export function SavedViewsSection({ expanded, activeId, counts, onSelect, onDeleted, onOpenDialog }: SavedViewsSectionProps) {
  const { t } = useTranslation('convList')
  const { data } = useInboxViews()
  const del = useDeleteInboxView()
  const [open, setOpen] = useState(loadOpen)
  const views = data?.views ?? []
  const personal = views.filter((v) => v.scope === 'personal')
  const shared = views.filter((v) => v.scope === 'shared')

  const toggle = (k: 'personal' | 'shared') => {
    const next = { ...open, [k]: !open[k] }
    try { localStorage.setItem(OPEN_KEY, JSON.stringify(next)) } catch { /* bỏ qua */ }
    setOpen(next)
  }
  const createNew = () => onOpenDialog({ open: true, mode: 'create', conditions: [], logic: 'and' })
  const remove = async (v: InboxSavedView) => {
    if (!window.confirm(t('views.deleteConfirm', { name: v.name }))) return
    try {
      await del.mutateAsync(v.id)
      onDeleted(v.id)
      toast.success(t('views.deleted'))
    } catch (e) {
      toast.error(apiError(e))
    }
  }

  // ── Bản thu gọn: một nút mở menu các chế độ xem ──
  if (!expanded) {
    const active = views.find((v) => v.id === activeId)
    return (
      <DropdownMenu>
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label={t('views.custom')}
                className={cn(
                  'relative flex h-10 w-10 shrink-0 items-center justify-center rounded-lg transition-colors',
                  active ? 'bg-card text-primary shadow-sm' : 'text-muted-foreground hover:bg-card/70 hover:text-foreground',
                )}
              >
                <Star className="h-5 w-5" />
              </button>
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent side="right">{active ? active.name : t('views.custom')}</TooltipContent>
        </Tooltip>
        <DropdownMenuContent side="right" align="start" className="max-h-80 w-60 overflow-y-auto">
          <DropdownMenuLabel>{t('views.personalGroup')}</DropdownMenuLabel>
          {personal.length === 0 && <p className="px-2 pb-1 text-xs text-muted-foreground">{t('views.noPersonal')}</p>}
          {personal.map((v) => (
            <DropdownMenuItem key={v.id} onClick={() => onSelect(v)} className={cn(v.id === activeId && 'bg-accent text-primary')}>
              <Star className="h-4 w-4 text-amber-500" />
              <span className="truncate">{v.name}</span>
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuLabel>{t('views.sharedGroup')}</DropdownMenuLabel>
          {shared.length === 0 && <p className="px-2 pb-1 text-xs text-muted-foreground">{t('views.noShared')}</p>}
          {shared.map((v) => (
            <DropdownMenuItem key={v.id} onClick={() => onSelect(v)} className={cn(v.id === activeId && 'bg-accent text-primary')}>
              <Star className="h-4 w-4 text-primary" />
              <span className="truncate">{v.name}</span>
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={createNew}>
            <Plus className="h-4 w-4" /> {t('views.titleCreate')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    )
  }

  const group = (key: 'personal' | 'shared', label: string, list: InboxSavedView[]) => (
    <div>
      <button
        type="button"
        onClick={() => toggle(key)}
        aria-expanded={open[key]}
        className="flex h-8 w-full items-center gap-1.5 rounded-md px-2.5 text-xs text-muted-foreground hover:bg-card/70"
      >
        {open[key] ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        <span className="flex-1 truncate text-left">{label}</span>
      </button>
      {open[key] && (
        <div className="space-y-0.5 pl-3">
          {list.length === 0 ? (
            <EmptyViews label={key === 'personal' ? t('views.noPersonal') : t('views.noShared')} />
          ) : (
            list.map((v) => (
              <ViewRow
                key={v.id}
                view={v}
                active={v.id === activeId}
                count={counts?.[v.id] ?? undefined}
                onSelect={() => onSelect(v)}
                onRename={() => onOpenDialog({ open: true, mode: 'rename', view: v })}
                onEdit={() => onOpenDialog({ open: true, mode: 'edit', view: v })}
                onDelete={() => remove(v)}
              />
            ))
          )}
        </div>
      )}
    </div>
  )

  return (
    <div className="mt-2 border-t pt-2">
      <div className="group/views relative flex h-9 items-center px-2.5">
        <span className="flex-1 truncate text-sm font-medium text-foreground/85">{t('views.custom')}</span>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              aria-label={t('views.titleCreate')}
              onClick={createNew}
              className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-card hover:text-primary"
            >
              <Plus className="h-4 w-4" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="top">{t('views.titleCreate')}</TooltipContent>
        </Tooltip>
      </div>
      {group('personal', t('views.personalGroup'), personal)}
      {group('shared', t('views.sharedGroup'), shared)}
    </div>
  )
}

function ViewRow({
  view,
  active,
  count,
  onSelect,
  onRename,
  onEdit,
  onDelete,
}: {
  view: InboxSavedView
  active: boolean
  count?: number
  onSelect: () => void
  onRename: () => void
  onEdit: () => void
  onDelete: () => void
}) {
  const { t } = useTranslation('convList')
  return (
    <div className="group/view relative">
      <button
        type="button"
        onClick={onSelect}
        aria-pressed={active}
        title={view.scope === 'shared' && view.ownerName ? t('views.createdBy', { name: view.ownerName }) : undefined}
        className={cn(
          'flex h-9 w-full items-center gap-2.5 rounded-md px-2.5 text-sm transition-colors',
          view.canEdit && 'pr-9',
          active ? 'bg-accent font-medium text-primary' : 'text-foreground/85 hover:bg-card/70',
        )}
      >
        <Star className={cn('h-4 w-4 shrink-0', view.scope === 'shared' ? 'text-primary' : 'text-amber-500', active && 'fill-current')} />
        <span className="flex-1 truncate text-left">{view.name}</span>
        {count ? (
          <span className={cn('tabular-nums text-muted-foreground', view.canEdit && '[@media(hover:hover)]:group-hover/view:invisible')}>
            {count > 99 ? '99+' : count}
          </span>
        ) : null}
      </button>
      {view.canEdit && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label={t('views.rowActions', { name: view.name })}
              className="absolute right-1 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-md text-muted-foreground opacity-0 transition-opacity hover:bg-card hover:text-foreground focus-visible:opacity-100 group-hover/view:opacity-100 data-[state=open]:opacity-100 [@media(hover:none)]:opacity-100"
            >
              <MoreHorizontal className="h-4 w-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="right" align="start" className="w-48">
            <DropdownMenuItem onClick={onRename}>
              <Pencil className="h-4 w-4" /> {t('common:actions.rename')}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onEdit}>
              <Filter className="h-4 w-4" /> {t('views.editConditions')}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onDelete} className="text-destructive focus:text-destructive">
              <Trash2 className="h-4 w-4" /> {t('common:actions.delete')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  )
}

/** Trạng thái trống có hình minh hoạ nhỏ (kiểu SaleSmartly). */
function EmptyViews({ label }: { label: string }) {
  return (
    <div className="flex flex-col items-center gap-1.5 px-2 py-3 text-center">
      <span className="relative flex h-10 w-12 items-center justify-center">
        <span className="absolute inset-x-1 bottom-0 top-2 rounded-md border-2 border-dashed border-muted-foreground/30" />
        <Star className="relative h-4 w-4 text-muted-foreground/50" />
      </span>
      <span className="text-xs text-muted-foreground">{label}</span>
    </div>
  )
}
